import styled, { css } from "styled-components";
import { focusOutlineRule } from "styles/focus";
import { FIELD_HEIGHT } from "styles/sizes";
import { srOnly } from "../SrOnly";

export const Fieldset = styled.fieldset`
  margin: 0;
  padding: 0;
  border: 0;
  min-width: 0;
`;

// Same look as the LabelText above a kit Input.
export const Legend = styled.legend`
  padding: 0;
  margin-bottom: 6px;
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${({ theme }) => theme.textMuted};
`;

// One equal column per option (--count); the thumb slides under the checked
// one (--index). Both custom properties are set inline by the component.
// Isolated so the segments' z-index (above the thumb) stays local and
// nothing outside, like a sticky form footer, ends up painted beneath them.
// The segment look, shared with the controls that cannot be a radio group
// (icon segments, the filter bar): the track's box and a segment's text.
export const segmentTrack = css`
  height: ${FIELD_HEIGHT}px;
  padding: 3px;
  border: 1px solid ${({ theme }) => theme.formBorder};
  border-radius: 2px;
  background-color: ${({ theme }) => theme.inputBg};
  box-sizing: border-box;
`;

export const segmentItem = css<{ $selected?: boolean }>`
  font-size: 14px;
  font-weight: ${({ $selected }) => ($selected ? 600 : 500)};
  color: ${({ theme, $selected }) =>
    $selected ? theme.primaryDark : theme.textSecondary};
  transition: color 0.2s ease-in-out;

  &:hover {
    color: ${({ theme, $selected }) =>
      $selected ? theme.primaryDark : theme.textMain};
  }
`;

export const Track = styled.div`
  position: relative;
  isolation: isolate;
  display: grid;
  grid-template-columns: repeat(var(--count), minmax(0, 1fr));
  ${segmentTrack}
`;

export const Thumb = styled.div`
  position: absolute;
  top: 3px;
  bottom: 3px;
  left: 3px;
  width: calc((100% - 6px) / var(--count));
  background-color: ${({ theme }) => theme.primary10};
  transform: translateX(calc(var(--index) * 100%));
  transition: transform 0.2s ease-in-out;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

// Visually hidden, not display: none, so the native radio stays focusable
// and keeps its arrow-key behaviour; the Segment label draws it.
export const SegmentInput = styled.input`
  ${srOnly}
`;

interface SegmentProps {
  $selected: boolean;
}

export const Segment = styled.label<SegmentProps>`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  padding: 0 12px;
  cursor: pointer;
  white-space: nowrap;
  ${segmentItem}

  ${SegmentInput}:focus-visible + & {
    ${focusOutlineRule}
  }

  ${SegmentInput}:disabled + & {
    cursor: not-allowed;
    opacity: 0.5;
  }

  ${(props) => props.theme.xs`
    padding: 0 8px;
    font-size: 13px;
  `}
`;
